import { LoadingButton } from '@mui/lab';
import { Box, Chip, Divider, Stack, Typography } from '@mui/material';
import AgentPickerDialog from 'components/private/Modals/operation/AgentPickerDialog';
import DepartureTakeFlow from 'components/private/Modals/operation/departure/Workspace/DepartureTake/DepartureTakeFlow';
import FinishOperationModal from 'components/private/Modals/operation/FinishOperationModal';
import RequireOperationSettings from 'components/shared/RequiredOpertaionSettings/RequireOperationSettings';
import SpinnerLoader from 'components/shared/SpinnerLoader/SpinnerLoader';
import { getOperationStatus } from 'hooks/operation/useDepartureOperationTableColumns';
import useDepartureWorkspace from 'hooks/operation/useDepartureWorkspace';
import ProductList from 'pages/Operation/ProductList';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { updateQueryParams } from 'utils/updateQueryParams';

import { AgentDetail } from './AgentDetail';
import { DepartureDock } from './DepartureDock';
import ExitList from './ExitList';
import { ShoppingCartDetail } from './ShoppingCartDetail';
import { WorkspaceSidebar } from './WorkspaceSidebar';

const SIDEBAR_SECTION = {
  EXIT_LIST: 'exitList',
  PRODUCT_LIST: 'productList',
  DOCK: 'dock',
  CART: 'cart',
  AGENT: 'agent',
};

const normalizeTaskList = payload => {
  if (!payload) return [];
  if (Array.isArray(payload.productPackagingList)) {
    return payload.productPackagingList;
  }
  if (Array.isArray(payload)) return payload;
  return [];
};

const DepartureOperationWorkspace = () => {
  const { guid } = useParams();

  const [selectedSection, setSelectedSection] = useState({
    type: SIDEBAR_SECTION.EXIT_LIST,
  });
  const [expandedAgents, setExpandedAgents] = useState(true);
  const [isAddAgentModalOpen, setIsAddAgentModalOpen] = useState(false);
  const [isFinishModalOpen, setIsFinishModalOpen] = useState(false);
  const [takeProduct, setTakeProduct] = useState(null);

  const [agentTasks, setAgentTasks] = useState([]);
  const [agentDetailLoading, setAgentDetailLoading] = useState(false);
  const [cartProducts, setCartProducts] = useState([]);
  const [cartDetailLoading, setCartDetailLoading] = useState(false);

  const {
    loading,
    actionLoading,
    workspace,
    title,
    state,
    agents,
    exitProducts,
    dockerBaskets,
    canMutate,
    canSubmitOperation,
    canSubmitVoucher,
    autoInitAll,
    autoInit,
    initTask,
    getAllLocality,
    insertAgent,
    deleteAgent,
    removeAgentTask,
    getAgentDetail,
    runTask,
    runAllTasks,
    getShoppingCartDetail,
    confirmMission,
    confirmAllMissions,
    submitOperation,
    submitVoucher,
    getAvailableAgents,
  } = useDepartureWorkspace(guid);

  const selectedAgent = useMemo(() => {
    if (selectedSection.type !== SIDEBAR_SECTION.AGENT) return null;
    return agents.find(
      agent =>
        (agent.operationAgentGuid || agent.guid) === selectedSection.guid,
    );
  }, [selectedSection, agents]);

  const selectedCart = useMemo(() => {
    if (selectedSection.type !== SIDEBAR_SECTION.CART) return null;
    return dockerBaskets.find(cart => cart.guid === selectedSection.guid);
  }, [selectedSection, dockerBaskets]);

  const sectionTitle = useMemo(() => {
    switch (selectedSection.type) {
      case SIDEBAR_SECTION.EXIT_LIST:
        return 'لیست جمع آوری';
      case SIDEBAR_SECTION.DOCK:
        return 'بارانداز';
      case SIDEBAR_SECTION.PRODUCT_LIST:
        return 'لیست محصول';
      case SIDEBAR_SECTION.CART: {
        if (selectedCart?.agentName && selectedCart?.title) {
          return `${selectedCart.agentName} - ${selectedCart.title}`;
        }
        return selectedCart?.title || selectedSection.title || 'سبد';
      }
      case SIDEBAR_SECTION.AGENT:
        return selectedAgent?.name || selectedSection.title || 'ایحنت';
      default:
        return '';
    }
  }, [selectedSection, selectedCart, selectedAgent]);

  const loadAgentDetail = useCallback(
    async operationAgentGuid => {
      if (!operationAgentGuid) return;
      setAgentDetailLoading(true);
      setAgentTasks([]);
      const detail = await getAgentDetail(operationAgentGuid);
      setAgentTasks(normalizeTaskList(detail));
      setAgentDetailLoading(false);
    },
    [getAgentDetail],
  );

  const loadCartDetail = useCallback(
    async shoppingCartGuid => {
      if (!shoppingCartGuid) return;
      setCartDetailLoading(true);
      setCartProducts([]);
      const detail = await getShoppingCartDetail(shoppingCartGuid);
      setCartProducts(normalizeTaskList(detail));
      setCartDetailLoading(false);
    },
    [getShoppingCartDetail],
  );

  const resetDetailViews = useCallback(() => {
    setAgentTasks([]);
    setCartProducts([]);
  }, []);

  const handleSelectSidebarItem = useCallback(
    (type, item) => {
      switch (type) {
        case SIDEBAR_SECTION.EXIT_LIST:
          resetDetailViews();
          setSelectedSection({ type: SIDEBAR_SECTION.EXIT_LIST });
          updateQueryParams({
            selectedTab: SIDEBAR_SECTION.EXIT_LIST,
            selectedAgent: null,
            selectedCart: null,
          });
          break;

        case SIDEBAR_SECTION.PRODUCT_LIST:
          setSelectedSection({ type: SIDEBAR_SECTION.PRODUCT_LIST });
          updateQueryParams({
            selectedTab: SIDEBAR_SECTION.PRODUCT_LIST,
            selectedCart: null,
          });
          break;

        case SIDEBAR_SECTION.DOCK:
          resetDetailViews();
          setSelectedSection({ type: SIDEBAR_SECTION.DOCK });
          updateQueryParams({
            selectedTab: SIDEBAR_SECTION.DOCK,
            selectedAgent: null,
            selectedCart: null,
          });
          break;

        case SIDEBAR_SECTION.CART: {
          const cartGuid = item?.guid;
          setAgentTasks([]);
          setSelectedSection({
            type: SIDEBAR_SECTION.CART,
            guid: cartGuid,
            title:
              item?.agentName && item?.title
                ? `${item.agentName} - ${item.title}`
                : item?.title,
          });
          updateQueryParams({
            selectedTab: SIDEBAR_SECTION.CART,
            selectedCart: cartGuid,
            selectedAgent: null,
          });
          loadCartDetail(cartGuid);
          break;
        }

        case SIDEBAR_SECTION.AGENT: {
          const operationAgentGuid = item?.operationAgentGuid || item?.guid;
          setCartProducts([]);
          setExpandedAgents(true);
          setSelectedSection({
            type: SIDEBAR_SECTION.AGENT,
            guid: operationAgentGuid,
            title: item?.name,
          });
          updateQueryParams({
            selectedTab: SIDEBAR_SECTION.AGENT,
            selectedAgent: operationAgentGuid,
            selectedCart: null,
          });
          loadAgentDetail(operationAgentGuid);
          break;
        }

        default:
          break;
      }
    },
    [loadAgentDetail, loadCartDetail, resetDetailViews],
  );

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const selectedTabParam = params.get('selectedTab');
    const selectedCartParam = params.get('selectedCart');
    const selectedAgentParam = params.get('selectedAgent');

    if (!selectedTabParam) return;

    if (selectedTabParam === SIDEBAR_SECTION.CART && selectedCartParam) {
      handleSelectSidebarItem(SIDEBAR_SECTION.CART, {
        guid: selectedCartParam,
      });
      return;
    }

    if (selectedTabParam === SIDEBAR_SECTION.AGENT && selectedAgentParam) {
      handleSelectSidebarItem(SIDEBAR_SECTION.AGENT, {
        operationAgentGuid: selectedAgentParam,
        guid: selectedAgentParam,
      });
      return;
    }

    if (
      selectedTabParam === SIDEBAR_SECTION.EXIT_LIST ||
      selectedTabParam === SIDEBAR_SECTION.DOCK
    ) {
      handleSelectSidebarItem(selectedTabParam);
    }
  }, []);

  const handleToggleAgents = () => {
    setExpandedAgents(prev => !prev);
  };

  const handleConfirmFinishOperation = async () => {
    const ok = await submitOperation();
    if (ok) setIsFinishModalOpen(false);
  };

  const handleDeleteAgent = async operationAgentGuid => {
    const ok = await deleteAgent(operationAgentGuid);
    if (ok) {
      resetDetailViews();
      setSelectedSection({ type: SIDEBAR_SECTION.EXIT_LIST });
      updateQueryParams({
        selectedTab: SIDEBAR_SECTION.EXIT_LIST,
        selectedAgent: null,
        selectedCart: null,
      });
    }
  };

  const handleConfirmMission = async taskGuid => {
    const ok = await confirmMission(taskGuid);
    if (ok && selectedSection.type === SIDEBAR_SECTION.CART) {
      await loadCartDetail(selectedSection.guid);
    }
  };

  const handleConfirmAllMissions = async operationShoppingCartGuid => {
    const ok = await confirmAllMissions(operationShoppingCartGuid);
    if (ok) {
      resetDetailViews();
      setSelectedSection({ type: SIDEBAR_SECTION.EXIT_LIST });
      updateQueryParams({
        selectedTab: SIDEBAR_SECTION.EXIT_LIST,
        selectedAgent: null,
        selectedCart: null,
      });
    }
  };

  const handleRunTask = async taskOrGuid => {
    const taskGuid =
      typeof taskOrGuid === 'string' ? taskOrGuid : taskOrGuid?.guid;
    if (!taskGuid) return;
    const ok = await runTask(taskGuid);
    if (ok && selectedSection.type === SIDEBAR_SECTION.AGENT) {
      await loadAgentDetail(selectedSection.guid);
    }
  };

  const handleRunAllTasks = async agentGuid => {
    const ok = await runAllTasks(agentGuid);
    if (ok && selectedSection.type === SIDEBAR_SECTION.AGENT) {
      await loadAgentDetail(selectedSection.guid);
    }
  };

  const handleRemoveAgentTask = async taskGuid => {
    const ok = await removeAgentTask(taskGuid);
    if (ok && selectedSection.type === SIDEBAR_SECTION.AGENT) {
      await loadAgentDetail(selectedSection.guid);
    }
  };

  const status = getOperationStatus({
    state,
    hasVoucher: workspace?.hasVoucher,
  });

  // eslint-disable-next-line complexity
  const renderMainContent = () => {
    if (loading && !workspace) return <SpinnerLoader />;
    if (!workspace) {
      return (
        <Typography color="text.secondary" textAlign="center" py={4}>
          اطلاعات فضای کاری یافت نشد.
        </Typography>
      );
    }

    switch (selectedSection.type) {
      case SIDEBAR_SECTION.EXIT_LIST:
        return (
          <ExitList
            products={exitProducts}
            canMutate={canMutate}
            actionLoading={actionLoading}
            onAutoInitAll={autoInitAll}
            onTakeProduct={setTakeProduct}
          />
        );

      case SIDEBAR_SECTION.DOCK:
        return (
          <DepartureDock
            baskets={dockerBaskets}
            onOpenBasket={basket =>
              handleSelectSidebarItem(SIDEBAR_SECTION.CART, basket)
            }
          />
        );

      case SIDEBAR_SECTION.PRODUCT_LIST:
        return (
          <ProductList
            operationType="departure"
            operationGuid={workspace.guid}
            state={state}
            reportDisabled
          />
        );

      case SIDEBAR_SECTION.CART:
        return selectedCart || selectedSection.guid ? (
          <ShoppingCartDetail
            cart={selectedCart || { guid: selectedSection.guid }}
            products={cartProducts}
            detailLoading={cartDetailLoading}
            canMutate={canMutate}
            actionLoading={actionLoading}
            onConfirmMission={handleConfirmMission}
            onConfirmAllMissions={handleConfirmAllMissions}
          />
        ) : (
          <Typography color="text.secondary" textAlign="center" py={4}>
            سبد انتخاب‌شده یافت نشد.
          </Typography>
        );

      case SIDEBAR_SECTION.AGENT:
        return selectedAgent || selectedSection.guid ? (
          <AgentDetail
            agent={
              selectedAgent || {
                operationAgentGuid: selectedSection.guid,
                title: selectedSection.title,
              }
            }
            tasks={agentTasks}
            detailLoading={agentDetailLoading}
            canMutate={canMutate}
            actionLoading={actionLoading}
            onRunTask={handleRunTask}
            onRunAllTasks={handleRunAllTasks}
            onRemoveTask={handleRemoveAgentTask}
            onDeleteAgent={handleDeleteAgent}
          />
        ) : (
          <Typography color="text.secondary" textAlign="center" py={4}>
            PDA انتخاب‌شده یافت نشد.
          </Typography>
        );

      default:
        return null;
    }
  };

  return (
    <RequireOperationSettings>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          width: 'full',
          mt: 2,
        }}
      >
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Typography sx={{ fontSize: 18, fontWeight: '600' }}>
            عملیات خروج &quot;{title || workspace?.title}&quot;
          </Typography>
          <Chip size="small" color={status.color} label={status.label} />
        </Box>

        <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
          {canSubmitVoucher ? (
            <LoadingButton
              variant="contained"
              size="small"
              color="success"
              loading={loading || actionLoading}
              disabled={loading || actionLoading}
              onClick={submitVoucher}
            >
              ثبت سند
            </LoadingButton>
          ) : null}

          {canSubmitOperation ? (
            <LoadingButton
              variant="contained"
              color="warning"
              loading={loading || actionLoading}
              disabled={loading || actionLoading}
              onClick={() => setIsFinishModalOpen(true)}
            >
              اتمام عملیات
            </LoadingButton>
          ) : null}
        </Stack>
      </Box>

      <Divider orientation="horizontal" variant="fullWidth" sx={{ my: 2 }} />

      <Box sx={{ display: 'flex', height: '85vh', gap: 2 }}>
        <WorkspaceSidebar
          agents={agents}
          expandedAgents={expandedAgents}
          selectedSection={selectedSection}
          onSelectSidebarItem={handleSelectSidebarItem}
          onToggleAgents={handleToggleAgents}
          onAddAgent={() => setIsAddAgentModalOpen(true)}
          canAddAgent={canMutate}
        />

        <Divider orientation="vertical" variant="fullWidth" flexItem />

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            flex: {
              xs: '2',
              sm: '3',
              md: '4',
              lg: '6',
            },
            height: '100%',
            minHeight: 0,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              justifyContent: 'space-between',
              flexShrink: 0,
            }}
          >
            <Typography sx={{ fontSize: 18, fontWeight: '600' }}>
              {sectionTitle}{' '}
            </Typography>
          </Box>

          <Box sx={{ flex: 1, overflowY: 'auto', p: 1 }}>
            {renderMainContent()}
          </Box>
        </Box>
      </Box>

      <AgentPickerDialog
        open={isAddAgentModalOpen}
        onClose={() => setIsAddAgentModalOpen(false)}
        onSelectAgent={insertAgent}
        getAvailableAgents={getAvailableAgents}
        loading={actionLoading}
        title="افزودن انباردار"
        confirmLabel="افزودن"
      />

      <DepartureTakeFlow
        open={Boolean(takeProduct)}
        onClose={() => setTakeProduct(null)}
        product={takeProduct}
        agents={agents}
        actionLoading={actionLoading}
        getAllLocality={getAllLocality}
        onAutoInit={autoInit}
        onInitTask={initTask}
      />

      <FinishOperationModal
        open={isFinishModalOpen}
        onClose={() => setIsFinishModalOpen(false)}
        onConfirm={handleConfirmFinishOperation}
        loading={actionLoading}
        title={title}
      />
    </RequireOperationSettings>
  );
};

export default DepartureOperationWorkspace;
